import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { API_BASE_URL, authenticatedFetch } from '../utils/api';

const GroupContext = createContext(null);

export function useGroup() {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error('useGroup must be used within GroupProvider');
  return ctx;
}

const LS_KEY = 'cascade.currentGroup';

export function GroupProvider({ children }) {
  const [groups, setGroups] = useState([]);
  const [currentGroup, setCurrentGroup] = useState(() => {
    try {
      const v = localStorage.getItem(LS_KEY);
      return v || '';
    } catch (_) {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSuffix, setActiveSuffix] = useState('');

  const refreshGroups = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Resolve this user's active horizon first so the groups request matches the UI selector
      // (same source as /instances/set-active → cascade_user_config.current_suffix).
      const sfxResp = await authenticatedFetch(`${API_BASE_URL}/casc/v1/api/data-settings/active-suffix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const sfxJson = await sfxResp.json().catch(() => ({}));
      if (!sfxResp.ok || sfxJson?.success === false) {
        throw new Error(sfxJson?.error || 'Failed to load active horizon');
      }
      const sfx = sfxJson?.currentSuffix || sfxJson?.suffix || 'zagger_product_help';
      setActiveSuffix(sfx);

      // Omit all=true so the server applies ingestion_group_horizons_{suffix} filtering when rows exist;
      // when the join table is empty, the server keeps all groups for this suffix (legacy behavior).
      const groupsUrl = `${API_BASE_URL}/casc/v1/api/data-review/groups?suffix=${encodeURIComponent(sfx)}`;
      const resp = await authenticatedFetch(groupsUrl, { method: 'GET' });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.success === false) throw new Error(json?.error || 'Failed to load groups');
      const list = Array.isArray(json.groups) ? json.groups : [];
      setGroups(list);
      // The groups endpoint no longer returns a currentGroup; keep user's selection if present and valid,
      // otherwise default to the first available group's slug.
      setCurrentGroup(prev => {
        // keep previous if it still exists in the loaded list
        if (prev && list.some(g => (g?.group_slug || g?.id) === prev)) return prev;
        if (list.length > 0) return list[0]?.group_slug || list[0]?.id || '';
        return '';
      });
    } catch (e) {
      // Show a friendlier message for network-level failures (server unreachable)
      const isNetworkError = e instanceof TypeError && (e.message === 'Failed to fetch' || e.message.includes('NetworkError'));
      setError(isNetworkError ? 'Cannot reach server — check that the API is running.' : (e.message || 'Failed to load groups'));
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refreshGroups(); }, [refreshGroups]);

  // Listen for horizon change events (SuffixSelector updates DB then dispatches this)
  useEffect(() => {
    const handleHorizonChanged = async (event) => {
      console.log('[GroupContext] Horizon changed event received:', event.detail);
      await refreshGroups();
      console.log('[GroupContext] Context refreshed after horizon change');
    };

    window.addEventListener('horizonChanged', handleHorizonChanged);
    return () => {
      window.removeEventListener('horizonChanged', handleHorizonChanged);
    };
  }, [refreshGroups]);

  useEffect(() => {
    try { localStorage.setItem(LS_KEY, currentGroup || ''); } catch (_) {}
  }, [currentGroup]);

  const appendGroupToUrl = useCallback((url) => {
    try {
      const u = new URL(url, window.location.origin);
      // Only add to our API routes
      if (u.pathname.includes('/casc/v1/api/')) {
        if (currentGroup) u.searchParams.set('group', currentGroup);
      }
      return u.toString();
    } catch (_) {
      return url;
    }
  }, [currentGroup]);

  // Build a group-aware API URL using the canonical suffix/group path
  // Example: buildGroupApiUrl('/entries') => `${API_BASE_URL}/casc/v1/api/suffixes/{suffix}/groups/{group}/entries`
  const buildGroupApiUrl = useCallback((pathAfterGroup) => {
    const p = String(pathAfterGroup || '');
    const tail = p.startsWith('/') ? p : `/${p}`;
    // Use currentGroup if set; otherwise fall back to first available group's slug to avoid hitting an empty group path
    const effectiveGroup = currentGroup || (groups[0]?.group_slug || groups[0]?.id || '');
    return `${API_BASE_URL}/casc/v1/api/suffixes/${encodeURIComponent(activeSuffix)}/groups/${encodeURIComponent(effectiveGroup)}${tail}`;
  }, [activeSuffix, currentGroup, groups]);

  const value = useMemo(() => ({
    groups,
    currentGroup,
    setCurrentGroup,
    loading,
    error,
    refreshGroups,
    appendGroupToUrl,
    activeSuffix,
    buildGroupApiUrl,
  }), [groups, currentGroup, loading, error, refreshGroups, appendGroupToUrl, activeSuffix, buildGroupApiUrl]);

  return (
    <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
  );
}
